/**
 * <GameGlyph> — ce qui se gagne dans le jeu, dessiné plein : le pétale, l'éclair de l'XP, la
 * rosette du titre (molecule).
 *
 * Le livre dessine le jeu autrement que l'interface. L'iconographie (chap. 05) : « trait 1,5 px,
 * jamais de remplissage, 16 · 20 · 24 ». Le système de jeu (chap. 16) : une flamme, un pétale et
 * une rosette PLEINS, au corail, aussi hauts que le nombre qu'ils accompagnent. Ce glyphe-ci est
 * celui du jeu ; `<Icon>` reste celui de l'interface. C'est l'exception que `<Icon solid>` réserve
 * déjà à « une marque d'état où le remplissage porte l'information » : ici, ce que l'élève gagne.
 *
 * Avec ce qui existe, faute des glyphes du jeu dessinés par la marque :
 *  - le pétale est celui de l'app d'aujourd'hui (`assets/icons/generic/petal_new.png`), peint à la
 *    couleur du ton : sa silhouette, pas son rose ;
 *  - l'éclair est `bolt` de Lucide, rempli ;
 *  - la rosette est `award` de Lucide, remplie, avec l'étoile du badge du livre dedans.
 * Le livre les dessine en deux tons ; la pastille (`plate`, défaut) tient lieu du second : le ton
 * en calque derrière le glyphe plein, comme la tinte d'une chip.
 *
 * Trois tons, la règle du livre : `gain` (corail, « pour l'action » : ce qui reste à gagner, et ce
 * qui vient d'être gagné), `acquired` (vert, « pour l'acquis »), `muted` (une étape pas encore
 * débloquée : la forme sans la couleur).
 *
 * Décoratif : le nombre ou le mot à côté le dit. Jamais seul, jamais pressable.
 */
import React from "react"
import { Image, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import petalImage from "../../../../assets/icons/generic/petal_new.png"
import { Icon } from "../../atoms/icons/Icon"
import { type SemanticColorKey } from "../../theme/tokens/colors"

export type GameGlyphName = "petals" | "xp" | "title"

export type GameGlyphTone = "gain" | "acquired" | "muted"

/** Les tailles du jeu : une chip, une ligne, une carte, la tête d'un écran. */
export const GAME_GLYPH_SIZE = { chip: 16, row: 28, card: 36, hero: 56 } as const

const TONE_COLOR: Record<GameGlyphTone, SemanticColorKey> = {
    gain: "role",
    acquired: "success",
    muted: "text-muted",
}

/** L'α du calque de la pastille : celui des tintes de chip du livre, entre 14 et 25 %. */
const PLATE_ALPHA = 0.18
/** Le glyphe dans sa pastille. La rosette, haute et étroite, y prend plus de place pour peser autant. */
const GLYPH_IN_PLATE: Record<GameGlyphName, number> = { petals: 0.58, xp: 0.58, title: 0.72 }
/** L'étoile de la rosette : sa taille et son centre, en fraction du glyphe (le disque d'`award`). */
const STAR_SIZE = 0.36
const STAR_CENTER_Y = 0.335

export type GameGlyphProps = {
    name: GameGlyphName
    /** Côté de la boîte en px. Défaut `GAME_GLYPH_SIZE.row` (28). */
    size?: number
    /** Défaut `gain`. */
    tone?: GameGlyphTone
    /** La pastille teintée derrière le glyphe. Défaut `true`. Sans elle, le glyphe remplit la boîte. */
    plate?: boolean
    /**
     * L'encre du glyphe, à la place de celle du ton : dans une chip, celle de la chip (son texte),
     * pour que le glyphe se lise sur son aplat comme sur sa tinte.
     */
    color?: SemanticColorKey
    testID?: string
}

export function GameGlyph(props: GameGlyphProps) {
    const { name, size = GAME_GLYPH_SIZE.row, tone = "gain", plate = true, color: ink, testID } = props
    const color = ink ?? TONE_COLOR[tone]
    const glyph = plate ? Math.round(size * GLYPH_IN_PLATE[name]) : size

    return (
        <View testID={testID} style={styles.box(size)} importantForAccessibility="no-hide-descendants">
            {plate && <View style={styles.plate(size, color)} />}
            <Glyph name={name} size={glyph} color={color} />
        </View>
    )
}

function Glyph(props: { name: GameGlyphName; size: number; color: SemanticColorKey }) {
    const { name, size, color } = props
    const { theme } = useUnistyles()

    // La teinte passe par la prop, pas par la feuille : sur le web, Unistyles fait de la feuille une
    // classe CSS, et react-native-web ne lit `tintColor` que dans la prop ou un style en objet.
    if (name === "petals") {
        return (
            <Image
                source={petalImage}
                tintColor={theme.colors[color]}
                style={styles.petal(size)}
                resizeMode="contain"
            />
        )
    }
    if (name === "xp") return <Icon name="bolt" size={size} color={color} solid />

    const star = Math.round(size * STAR_SIZE)
    return (
        <View style={styles.rosette(size)}>
            <Icon name="award" size={size} color={color} solid />
            <View style={styles.star(size, star)}>
                <Icon name="star" size={star} color="white" solid />
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    box: (size: number) => ({
        width: size,
        height: size,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        flexShrink: 0,
    }),
    /** Le ton en calque : la même couleur en clair et sur la nuit, c'est la surface dessous qui change. */
    plate: (size: number, color: SemanticColorKey) => ({
        position: "absolute" as const,
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: theme.colors[color],
        opacity: PLATE_ALPHA,
    }),
    petal: (size: number) => ({
        width: size,
        height: size,
    }),
    rosette: (size: number) => ({
        width: size,
        height: size,
    }),
    star: (size: number, star: number) => ({
        position: "absolute" as const,
        left: (size - star) / 2,
        top: size * STAR_CENTER_Y - star / 2,
    }),
}))
